import { PATTERN_GROUPS, PATTERNS, type PatternFacts, type PatternGroup, type PatternScale } from './book.ts';

/**
 * The book read for a job (spec "Pattern Mapping" §4.2, §4.3): a pattern by its number, its scale and group, the
 * patterns linked to it either way, the building types and every pattern each could touch.
 */

/** A pattern by its number; undefined for a number the book does not have. */
export function patternOf(n: number): PatternFacts | undefined {
  return Number.isInteger(n) && n >= 1 && n <= PATTERNS.length ? PATTERNS[n - 1] : undefined;
}

/** The book's scale a number falls in: Towns 1–94, Buildings 95–204, Construction 205–253. */
export function scaleOf(n: number): PatternScale {
  return n <= 94 ? 'Towns' : n <= 204 ? 'Buildings' : 'Construction';
}

/** The book's group a pattern sits in. */
export function groupOf(n: number): PatternGroup | undefined {
  return patternOf(n) && PATTERN_GROUPS.find((g) => n >= g.from && n <= g.to);
}

/**
 * Every pattern linked to each, either way: what it names and what names it. Built on first use, so a page that never
 * reads the book does not carry it.
 */
let linked: ReadonlyMap<number, ReadonlySet<number>> | undefined;
function linkedOf(n: number): ReadonlySet<number> | undefined {
  if (!linked) {
    const m = new Map<number, Set<number>>(PATTERNS.map((p) => [p.n, new Set<number>()]));
    for (const p of PATTERNS) {
      for (const k of [...p.larger, ...p.smaller]) {
        if (k === p.n || !m.has(k)) continue;
        m.get(p.n)!.add(k);
        m.get(k)!.add(p.n);
      }
    }
    linked = m;
  }
  return linked.get(n);
}

/**
 * The patterns linked to one, as its panel lists them: the lower-numbered are the larger patterns it helps complete,
 * the higher the smaller patterns that complete it (the book runs from larger to smaller). None for a number it lacks.
 */
export function related(n: number): { larger: number[]; smaller: number[] } {
  const all = [...(linkedOf(n) ?? [])].sort((a, b) => a - b);
  return { larger: all.filter((k) => k < n), smaller: all.filter((k) => k > n) };
}

/**
 * Following the book down (spec §5.1): to a higher-numbered pattern a pattern's own closing names, or to a later
 * pattern whose opening names it. A pattern's closing naming an earlier one is a warning, not a way down. Built on
 * first use.
 */
let down: ReadonlyMap<number, readonly number[]> | undefined;
function downOf(n: number): readonly number[] {
  if (!down) {
    const m = new Map<number, Set<number>>(PATTERNS.map((p) => [p.n, new Set<number>()]));
    for (const p of PATTERNS) {
      for (const s of p.smaller) if (s > p.n && m.has(s)) m.get(p.n)!.add(s);
      for (const l of p.larger) if (l < p.n && m.has(l)) m.get(l)!.add(p.n);
    }
    down = new Map([...m].map(([k, v]) => [k, [...v].sort((a, b) => a - b)]));
  }
  return down.get(n) ?? [];
}

export const BUILDING_TYPE_KEYS = ['house', 'workshop', 'cottage'] as const;
export type BuildingTypeKey = (typeof BUILDING_TYPE_KEYS)[number];

export interface BuildingType {
  key: BuildingTypeKey;
  /** As the setup page ticks it. */
  name: string;
  /** The book's patterns it starts from. */
  from: readonly number[];
}

/** What can be built (spec §4.2; Jason, 2026-10-16: "are we building a workshop? Are we building a house?"). */
export const BUILDING_TYPES: Readonly<Record<BuildingTypeKey, BuildingType>> = {
  house: { key: 'house', name: 'A house', from: [76, 77, 78, 79] },
  workshop: { key: 'workshop', name: 'A workshop or studio', from: [157] },
  cottage: { key: 'cottage', name: 'A cottage', from: [154, 155] },
};

/**
 * Every pattern the buildings could touch (Jason, 2026-10-16: "I want all the patterns that a building could touch"):
 * each type's own patterns and all the book reaches down from them, the union of the types, in the book's order. None
 * ticked, none.
 */
export function patternPool(types: readonly BuildingTypeKey[]): number[] {
  const seen = new Set<number>();
  const next: number[] = [];
  const reach = (n: number) => {
    if (seen.has(n)) return;
    seen.add(n);
    next.push(n);
  };
  for (const t of new Set(types)) for (const n of BUILDING_TYPES[t].from) reach(n);
  while (next.length) for (const k of downOf(next.pop()!)) reach(k);
  return [...seen].sort((a, b) => a - b);
}
